import ContentUsageGridItem, {
  ContentUsageGridItemSkeleton,
} from "@/content-usage/modules/grid/ContentUsageGridItem"
import { ContentUsageGridItemFragment$key } from "@/content-usage/modules/grid/__generated__/ContentUsageGridItemFragment.graphql"
import { useActiveOrganization } from "@/core/context/ActiveOrganizationContext"
import DashboardBlockEmptyState from "@/dashboard/blocks/kinds/DashboardBlockEmptystate"
import { useDashboardContext } from "@/dashboard/context/DashboardContext"
import OccurrenceListItem from "@/product/course/event/list/item/OccurrenceListItem"
import { OccurrenceListItemFragment$key } from "@/product/course/event/list/item/__generated__/OccurrenceListItemFragment.graphql"
import Relay from "@/relay/relayUtils"
import useTrackUserActivity from "@/reporting/useTrackUserActivity"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import { DiscoCarousel, DiscoCarouselSkeleton, DiscoIcon, DiscoText } from "@disco-ui"
import usePermissions from "@utils/hook/usePermissions"
import { graphql, useLazyLoadQuery, usePaginationFragment } from "react-relay"
import DashboardBlockAdminDropdown from "../../DashboardBlockAdminDropdown"
import { RecentlyViewedDashboardBlockContentPaginationFragment$key } from "./__generated__/RecentlyViewedDashboardBlockContentPaginationFragment.graphql"
import { RecentlyViewedDashboardBlockContentPaginationQuery } from "./__generated__/RecentlyViewedDashboardBlockContentPaginationQuery.graphql"
import {
  DashboardBlockPosition,
  RecentlyViewedDashboardBlockContentQuery,
} from "./__generated__/RecentlyViewedDashboardBlockContentQuery.graphql"
import { RecentlyViewedDashboardBlockFragment$data } from "./__generated__/RecentlyViewedDashboardBlockFragment.graphql"

interface Props {
  block: RecentlyViewedDashboardBlockFragment$data
}

function RecentlyViewedDashboardBlockContent({ block }: Props) {
  const classes = useStyles()
  const trackUserActivity = useTrackUserActivity()
  const { isOneColumn } = useDashboardContext()!
  const activeOrganization = useActiveOrganization()
  const permissions = usePermissions(activeOrganization)

  const { recentlyViewedBlock } =
    useLazyLoadQuery<RecentlyViewedDashboardBlockContentQuery>(
      graphql`
        query RecentlyViewedDashboardBlockContentQuery($id: ID!, $first: Int) {
          recentlyViewedBlock: node(id: $id) {
            ... on RecentlyViewedDashboardBlock {
              id
              position
              ...RecentlyViewedDashboardBlockContentPaginationFragment
                @arguments(first: $first)
            }
          }
        }
      `,
      {
        id: block.id,
        first: 10,
      },
      {
        fetchPolicy: "network-only",
      }
    )

  const { data, loadNext, hasNext, hasPrevious, isLoadingNext } = usePaginationFragment<
    RecentlyViewedDashboardBlockContentPaginationQuery,
    RecentlyViewedDashboardBlockContentPaginationFragment$key
  >(
    graphql`
      fragment RecentlyViewedDashboardBlockContentPaginationFragment on RecentlyViewedDashboardBlock
      @refetchable(queryName: "RecentlyViewedDashboardBlockContentPaginationQuery")
      @argumentDefinitions(first: { type: "Int" }, after: { type: "String" }) {
        id
        recentlyViewedContentUsages(first: $first, after: $after)
          @connection(
            key: "RecentlyViewedDashboardBlockContent__recentlyViewedContentUsages"
          ) {
          __id
          totalCount
          edges {
            node {
              __typename
              id
              product {
                slug
              }
              viewerLastContentView {
                eventDatetime
              }
              ...ContentUsageGridItemFragment
            }
          }
        }
        recentlyViewedOccurrences(first: $first, after: $after) {
          __id
          totalCount
          edges {
            node {
              __typename
              id
              product {
                slug
              }
              recording {
                mediaAssetUsage {
                  viewerAssetVideoProgress {
                    updatedAt
                  }
                }
              }
              ...OccurrenceListItemFragment
            }
          }
        }
      }
    `,
    recentlyViewedBlock
  )

  // Alternate the order of recently viewed content usages and event recordings
  const recentlyViewedContentUsages = Relay.connectionToArray(
    data?.recentlyViewedContentUsages
  )
  const recentlyViewedOccurrences = Relay.connectionToArray(
    data?.recentlyViewedOccurrences
  )

  // Sort by most to least recently viewed
  const recentlyViewed = [
    ...recentlyViewedContentUsages,
    ...recentlyViewedOccurrences,
  ].sort((a, b) => {
    const aLastViewedDate =
      a.__typename === "ContentUsage"
        ? a.viewerLastContentView?.eventDatetime
        : a.recording?.mediaAssetUsage?.viewerAssetVideoProgress?.updatedAt
    const bLastViewedDate =
      b.__typename === "ContentUsage"
        ? b.viewerLastContentView?.eventDatetime
        : b.recording?.mediaAssetUsage?.viewerAssetVideoProgress?.updatedAt

    if (aLastViewedDate && bLastViewedDate) {
      return new Date(bLastViewedDate).getTime() - new Date(aLastViewedDate).getTime()
    }
    return 0
  })

  if (!recentlyViewed.length && !permissions.has("dashboard.manage")) return null

  // recently viewed empty state
  if (!recentlyViewed.length && permissions.has("dashboard.manage"))
    return (
      <div className={classes.emptyStateContainer}>
        <div className={classes.emptyStateTitle}>
          <DiscoText variant={"body-md-700"}>{"Recently Viewed"}</DiscoText>
          <DashboardBlockAdminDropdown dashboardBlockKey={block} />
        </div>
        <DashboardBlockEmptyState
          testid={"DashboardBlockEmptyState.recently_viewed"}
          subtitle={"You have not viewed any content recently."}
          icon={<DiscoIcon icon={"file"} />}
        />
      </div>
    )

  const totalCount =
    data!.recentlyViewedOccurrences.totalCount +
    data!.recentlyViewedContentUsages.totalCount

  return (
    <DiscoCarousel
      title={"Recently Viewed"}
      slidesPerView={getSlidesPerView(isOneColumn, block.position)}
      centeredSlides={false}
      data={recentlyViewed}
      classes={{ slide: classes.item, header: classes.header }}
      item={(item, idx) => {
        if (Relay.isNodeType(item, "ContentUsage")) {
          return (
            <ContentUsageGridItem
              key={item.id}
              isDashboardBlock
              contentUsageKey={item as ContentUsageGridItemFragment$key}
              testid={`RecentlyViewedDashboardBlockContent.item-${idx}`}
              hideMedia
              showOverflow={false}
              navigateOnClick
              onClick={handleTrackUserActivity}
              showLocation
              hideFooter
            />
          )
        } else if (Relay.isNodeType(item, "Occurrence")) {
          return (
            <OccurrenceListItem
              testid={`RecentlyViewedDashboardBlockContent.card-${idx}`}
              variant={"card"}
              occurrenceKey={item as OccurrenceListItemFragment$key}
              navigateOnClick
              hideCover
              hideHosts
              hideEventDate
              onClick={handleTrackUserActivity}
            />
          )
        }
        return <></>
      }}
      totalCount={totalCount}
      moreActions={<DashboardBlockAdminDropdown dashboardBlockKey={block} />}
      refetch={{
        hasNext,
        hasPrevious,
        loadMore: () => loadNext(6),
        isLoading: isLoadingNext,
      }}
    />
  )

  function handleTrackUserActivity() {
    trackUserActivity({
      kind: "clicked",
      entityId: block.id,
    })
  }
}

function getSlidesPerView(isOneColumn: boolean, position: DashboardBlockPosition) {
  if (isOneColumn) return 3.2
  return position === "main" ? 2.2 : 1
}

const useStyles = makeUseStyles((theme) => ({
  item: {
    width: "254px",
    maxHeight: "180px",
  },
  emptyStateContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 2, 0.5, 2),
  },
  emptyStateTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  header: {
    marginBottom: 0,
  },
}))

function RecentlyViewedDashboardBlockContentSkeleton({ block }: Props) {
  const { isOneColumn } = useDashboardContext()!
  return (
    <DiscoCarouselSkeleton
      showTitle
      item={<ContentUsageGridItemSkeleton height={"130px"} />}
      slidesPerView={getSlidesPerView(isOneColumn, block.position)}
    />
  )
}

export default Relay.withSkeleton({
  component: RecentlyViewedDashboardBlockContent,
  skeleton: RecentlyViewedDashboardBlockContentSkeleton,
})
